/**
 * Plan of Correction (POC) tracking service.
 *
 * After a survey, findings that require corrective action are tracked here.
 * Facilities can submit POCs through the prestudy portal, and surveyors
 * can review and verify them.
 */

import { invokeFunction } from '../api'
import { toApiRequestError } from '../errors'

// ── Types ───────────────────────────────────────────────────────────────────

export type CorrectionStatus = 'pending' | 'submitted' | 'accepted' | 'rejected' | 'verified' | 'overdue'

export interface SurveyCorrection {
  correction_id: string
  finding_id: string
  survey_id: string
  visit_id: string
  correction_plan: string | null
  responsible_party: string | null
  deadline: string | null
  status: CorrectionStatus
  submitted_at: string | null
  submitted_by_name: string | null
  verified_at: string | null
  verifier_notes: string | null
  created_at: string
}

// ── API ─────────────────────────────────────────────────────────────────────

export async function getCorrections(visitId: string, surveyId: string): Promise<SurveyCorrection[]> {
  const { data, error } = await invokeFunction<{ corrections: unknown[] }>('survey', {
    method: 'GET',
    path: `/visits/${visitId}/survey/${surveyId}/corrections`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load corrections')
  return (data?.corrections ?? []) as SurveyCorrection[]
}

export async function createCorrection(
  visitId: string,
  surveyId: string,
  findingId: string,
  input: { deadline?: string; responsible_party?: string },
): Promise<SurveyCorrection> {
  const { data, error } = await invokeFunction<unknown>('survey', {
    method: 'POST',
    path: `/visits/${visitId}/survey/${surveyId}/corrections`,
    body: { finding_id: findingId, ...input },
  })
  if (error || !data) throw toApiRequestError(error, 'Failed to create correction')
  return data as SurveyCorrection
}

export async function updateCorrection(
  visitId: string,
  surveyId: string,
  correctionId: string,
  input: Partial<Pick<SurveyCorrection, 'status' | 'correction_plan' | 'verifier_notes'>>,
): Promise<SurveyCorrection> {
  const { data, error } = await invokeFunction<unknown>('survey', {
    method: 'PATCH',
    path: `/visits/${visitId}/survey/${surveyId}/corrections/${correctionId}`,
    body: input,
  })
  if (error || !data) throw toApiRequestError(error, 'Failed to update correction')
  return data as SurveyCorrection
}
